/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useRef, useState} from 'react'
import { useIntl } from 'react-intl'
import { PageLink, PageTitle } from '../../../../../_metronic/layout/core/PageData'
import { ITicketFilter } from '../Ticket.model'
import { IFilterDto } from '../../../../common/model/Core.model'
import { useAppSelector, useAppDispatch } from '../../../store/Store'
import {
  selectTicket,
  selectTicketLoading,
  selectTickets,
  selectTicketsPagination,
  selectTicketTimeRangeSelection,
  setTicketTimeRangeSelection,
  selectTicketFiltersSelection,
  setTicketFiltersSelection,
  SerializableTimeRange,
} from '../Ticket.slice'
import {
  useTicketGetByIdMutation,
  useTicketLoadMoreMutation,
  useTicketSearchMutation,
} from '../Ticket.api'
import { SearchList } from '../../../../common/component/SearchList'
import { TagInputSearchControls } from '../../../../common/component/TagInputSearchControls'
import { TicketListItem } from '../components/TicketListItem'
import { TicketPreview } from '../components/TicketPreview'
import { MessageToast, MessageType } from '../../../../common/component/MessageToast'
import Toast from 'bootstrap/js/dist/toast'
import { TimeRange } from '../../../../common/component/DateRangeSelector'
import { getEntry, setEntry, removeEntry } from '../../../../service/LocalStorage.service'
import { recalibrateTimeRange } from '../../../../util/TimeRange.util'

const TICKET_TIME_RANGE_LS_KEY = 'ticket_timeRangeSelection'
const TICKET_FILTERS_LS_KEY = 'ticket_filtersSelection'

export const TicketDashboardPage: FC = () => {
  const intl = useIntl()
  const breadCrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({id: 'BREADCRUMBS.TICKETS'}),
      path: '/tickets',
      isSeparator: false,
      isActive: false,
    },
  ]

  const didFetchInitialData = useRef(false)
  const didFetchNextPage = useRef(false)
  const didFetchSearch = useRef(false)
  const [toastEntry, setToastEntry] = useState<[MessageType, string]>()
  const filterKeys = {
    defaultKey: ITicketFilter.CLIENT,
    defaultKeyList: [
      ITicketFilter.ID,
      ITicketFilter.CLIENT,
      ITicketFilter.TITLE,
      ITicketFilter.STATUS,
      ITicketFilter.CRITICALITY,
      ITicketFilter.FAMILY,
      ITicketFilter.AREA,
      ITicketFilter.BRAND,
      ITicketFilter.MODEL,
      ITicketFilter.SERIAL,
    ],
  }
  const [isSearchLoading, setIsSearchLoading] = useState(false)
  const [isSearchControlEnabled, setIsSearchControlEnabled] = useState(false)
  const [filterSuggestions, setFilterSuggestions] = useState<string[]>([])
  const [rowSelected, setRowSelected] = useState<string | undefined>(undefined)

  const dispatch = useAppDispatch()
  const timeRangeSelection = useAppSelector(selectTicketTimeRangeSelection)
  const filters = useAppSelector(selectTicketFiltersSelection)

  const sanitizePersistedFilters = (candidateFilters: unknown): IFilterDto[] => {
    if (!Array.isArray(candidateFilters)) {
      return []
    }
    return candidateFilters.filter((item): item is IFilterDto => {
      if (!item || typeof item !== 'object') {
        return false
      }
      const filter = item as IFilterDto
      return (
        typeof filter.id === 'string' &&
        filterKeys.defaultKeyList.includes(filter.id as ITicketFilter) &&
        typeof filter.value === 'string' &&
        typeof filter.editable === 'boolean'
      )
    })
  }

  const persistFiltersSelection = (nextFilters: IFilterDto[]) => {
    dispatch(setTicketFiltersSelection(nextFilters))
    if (nextFilters.length > 0) {
      setEntry(TICKET_FILTERS_LS_KEY, JSON.stringify(nextFilters))
    } else {
      removeEntry(TICKET_FILTERS_LS_KEY)
    }
  }

  const setFilters = (nextFilters: IFilterDto[]) => {
    persistFiltersSelection(nextFilters)
  }

  const handleTimeRangeChange = (range: TimeRange) => {
    const serializable: SerializableTimeRange = {
      timeRangeItem: range.timeRangeItem,
      from: range.from.getTime(),
      to: range.to.getTime(),
    }
    dispatch(setTicketTimeRangeSelection(serializable))
    setEntry(TICKET_TIME_RANGE_LS_KEY, JSON.stringify(serializable))
  }

  const [ticketSearch] = useTicketSearchMutation()
  const [ticketLoadMore] = useTicketLoadMoreMutation()
  const [ticketGetById] = useTicketGetByIdMutation()

  const tickets = useAppSelector(selectTickets)
  const ticketsPagination = useAppSelector(selectTicketsPagination)
  const ticket = useAppSelector(selectTicket)
  const ticketLoading = useAppSelector(selectTicketLoading)

  useEffect(() => {
    if (toastEntry) {
      const [messageType] = toastEntry
      const toastElement = document.getElementById('#CustomToast')
      const delay =
        messageType === MessageType.ERROR || messageType === MessageType.WARNING ? 10000 : 2000
      if (toastElement) {
        toastElement.addEventListener('hidden.bs.toast', () => setToastEntry(undefined))
        const toast = new Toast(toastElement, {autohide: true, delay})
        toast.show()
      }
    }
  }, [toastEntry])

  useEffect(() => {
    let initialRange: TimeRange | undefined
    let initialFilters: IFilterDto[] | undefined

    const savedFilters = getEntry(TICKET_FILTERS_LS_KEY)
    if (savedFilters) {
      try {
        const parsedFilters = JSON.parse(savedFilters)
        const sanitizedFilters = sanitizePersistedFilters(parsedFilters)
        persistFiltersSelection(sanitizedFilters)
        initialFilters = sanitizedFilters
      } catch {
        persistFiltersSelection([])
      }
    }

    const saved = getEntry(TICKET_TIME_RANGE_LS_KEY)
    if (saved) {
      try {
        const parsed: SerializableTimeRange = JSON.parse(saved)
        const recalibratedRange = recalibrateTimeRange(parsed)
        const recalibratedSerializable: SerializableTimeRange = {
          timeRangeItem: recalibratedRange.timeRangeItem,
          from: recalibratedRange.from.getTime(),
          to: recalibratedRange.to.getTime(),
        }
        dispatch(setTicketTimeRangeSelection(recalibratedSerializable))
        setEntry(TICKET_TIME_RANGE_LS_KEY, JSON.stringify(recalibratedSerializable))
        initialRange = recalibratedRange
      } catch {}
    }

    if (initialFilters && initialFilters.length > 0) {
      const filterIds = initialFilters.map((filter) => filter.id)
      setFilterSuggestions(
        filterKeys.defaultKeyList.filter((defaultKey) => !filterIds.includes(defaultKey))
      )
    } else {
      setFilterSuggestions(filterKeys.defaultKeyList)
    }

    fetchInitialData(initialRange, initialFilters)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchInitialData = async (rangeOverride?: TimeRange, filtersOverride?: IFilterDto[]) => {
    const range = rangeOverride ?? timeRangeSelection
    const requestFilters = filtersOverride ?? filters
    try {
      setIsSearchLoading(true)
      if (!didFetchInitialData.current) {
        const request: any = {
          timeRange: { ...range },
        }
        if (requestFilters && requestFilters.length > 0) {
          request.filters = requestFilters
        }
        const response: any = await ticketSearch(request)
        if (response.data) {
          setIsSearchControlEnabled(true)
          if (response.data.filters) {
            const filterIds = response.data.filters.map((filter) => filter.id)
            persistFiltersSelection(response.data.filters)
            setFilterSuggestions(
              filterKeys.defaultKeyList.filter((key) => !filterIds.includes(key))
            )
          } else {
            const filterIds = (requestFilters ?? []).map((filter) => filter.id)
            setFilterSuggestions(
              filterKeys.defaultKeyList.filter((key) => !filterIds.includes(key))
            )
          }
        }
      }
    } finally {
      setIsSearchLoading(false)
    }
  }

  const nextPageOnClickHandler = async () => {
    try {
      setIsSearchLoading(true)
      if (!didFetchNextPage.current) {
        await ticketLoadMore({
          pagination: {
            limit: ticketsPagination?.limit,
            sort: ticketsPagination?.sort,
            page: ticketsPagination?.page,
          },
          filters: filters,
          timeRange: {
            ...timeRangeSelection,
          },
        })
      }
    } finally {
      setIsSearchLoading(false)
    }
  }

  const searchOnClickHandler = async () => {
    try {
      setIsSearchLoading(true)
      if (!didFetchSearch.current) {
        await ticketSearch({
          pagination: {
            sort: ticketsPagination?.sort,
          },
          filters: filters,
          timeRange: {
            ...timeRangeSelection,
          },
        })
      }
    } finally {
      setIsSearchLoading(false)
    }
  }

  const resetSearchOnClickHandler = async () => {
    persistFiltersSelection([])
    setFilterSuggestions(filterKeys.defaultKeyList)
    await fetchInitialData(undefined, [])
  }

  const rowOnClickHandler = async (id: string) => {
    setRowSelected(id)
    if (!ticket || !ticket.ticket || ticket.ticket.id !== id) {
      await ticketGetById({id, fields: 'LIMITED'})
    }
  }

  return (
    <>
      {toastEntry && <MessageToast messageType={toastEntry[0]} message={toastEntry[1]} />}
      <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
      <div className='flex mt-10'>
        <div className='row g-5'>
          <SearchList
            loading={isSearchLoading}
            rowSelected={rowSelected}
            nextPage={ticketsPagination?.page}
            nextPageOnClickHandler={nextPageOnClickHandler}
            searchControlsComponent={
              <TagInputSearchControls
                loading={isSearchLoading}
                enabled={isSearchControlEnabled}
                filterKeys={filterKeys}
                filterSuggestions={filterSuggestions}
                filters={filters}
                setFilterSuggestions={setFilterSuggestions}
                setFilters={setFilters}
                createOnClickPath='/tickets/new'
                createOnClickOpenNew={false}
                searchOnClickHandler={searchOnClickHandler}
                resetSearchResetOnClickHandler={resetSearchOnClickHandler}
                timeRangeSelection={timeRangeSelection}
                timeRangeOnClickHandler={handleTimeRangeChange}
              ></TagInputSearchControls>
            }
            listComponent={tickets.map((item, index) => (
              <div key={index}>
                <TicketListItem
                  data={item}
                  rowSelected={rowSelected}
                  onRowClick={rowOnClickHandler}
                ></TicketListItem>
                {index !== 0 && <div className='separator'></div>}
              </div>
            ))}
          ></SearchList>

          {rowSelected && (
            <TicketPreview
              data={ticket}
              loading={ticketLoading}
              setRowSelected={setRowSelected}
              setToastEntry={setToastEntry}
            ></TicketPreview>
          )}
        </div>
      </div>
    </>
  )
}
